import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleCheck, Loader2 } from 'lucide-react';
import { useAuthContext } from 'modules/auth';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS } from 'modules/auth/permissions';
import { FEATURES, useFeature, useOpenUpgradeForFeature } from 'modules/entitlements';
import * as Grading from 'modules/grading';
import * as TestModule from 'modules/test';
import type { AiGradeSuggestion } from 'modules/grading/api';
import { toast } from 'components/ui/use-toast';
import { Button } from 'components/ui/button';
import { Panel } from 'components/ui/panel';
import Avatar from 'components/ui/avatar';
import config from 'config';
import { MobileScreen, ScreenHeader, ScreenBody, FullScreen, MobileSkeleton, MobileEmpty, Segmented, M } from 'components/mobile';
import {
  AiSuggestionBox,
  GraderNote,
  GradingRubric,
  GradingStatus,
  answerLabel,
  answerToText,
  buildQueueItems,
  groupQueue,
  questionNumberOf,
  questionNumbers,
  questionTypeLabelKey,
  type GradingMode,
  type QueueItem,
  type RubricCriterion,
} from 'components/result/grading-parts';
import { IeltsBandGrader } from 'modules/standardizedExam/components/grading/IeltsBandGrader';

/** Queue panels + full-screen grader. Reuses the exact grading hooks, so the data path never diverges. */
export function GradingMobile() {
  const { t, i18n } = useTranslation();
  const { state: authState } = useAuthContext();
  const canGrade = usePermission(PERMISSIONS.GRADING_GRADE);
  const canAiGrading = useFeature(FEATURES.AI_GRADING);
  const openUpgrade = useOpenUpgradeForFeature();
  const orgId =
    (typeof localStorage !== 'undefined' ? localStorage.getItem(config.api.active_org_key) : null) ||
    authState.organization?.id ||
    undefined;

  const { data: pending = [], isLoading } = Grading.usePendingGrading(orgId);
  const gradeManual = Grading.useGradeManual();
  const aiSuggest = Grading.useAiSuggest();

  const [mode, setMode] = useState<GradingMode>('question');
  const [openId, setOpenId] = useState<string | null>(null);
  const [graded, setGraded] = useState<Set<string>>(new Set());

  // Per-answer scoring state for the open grader.
  const [awarded, setAwarded] = useState<Record<string, number>>({});
  const [notes, setNotes] = useState('');
  const [aiScore, setAiScore] = useState<number | null>(null);
  const [suggestion, setSuggestion] = useState<AiGradeSuggestion | null>(null);

  const items = useMemo(() => buildQueueItems(pending, t), [pending, t]);
  const numbers = useMemo(() => questionNumbers(items), [items]);
  const groups = useMemo(() => groupQueue(items, mode, numbers), [items, mode, numbers]);
  // Save & next and Skip walk the rows in the order they are shown.
  const ordered = useMemo(() => groups.flatMap((group) => group.items), [groups]);
  const numberByAnswer = useMemo(
    () => new Map(items.map((item, index) => [item.answerId, questionNumberOf(numbers, item, index)])),
    [items, numbers],
  );
  const numberOf = (item: QueueItem) => numberByAnswer.get(item.answerId) ?? 0;

  // This session's progress — the pending endpoint returns only what is still pending.
  const gradedCount = graded.size;
  const remaining = items.filter((item) => !graded.has(item.answerId)).length;
  const sessionTotal = remaining + gradedCount;
  const progressPct = sessionTotal > 0 ? Math.round((gradedCount / sessionTotal) * 100) : 0;

  const active = items.find((it) => it.answerId === openId) ?? null;

  const { item: activeTest } = TestModule.Hooks.useSingle({
    id: active?.testId || '',
    enabled: Boolean(active?.testId),
  });
  const rubric = useMemo<RubricCriterion[]>(() => {
    const r = (activeTest as any)?.config?.grading?.feedback?.rubric;
    return Array.isArray(r) ? r : [];
  }, [activeTest]);

  // Reset scoring state whenever a different answer opens.
  useEffect(() => {
    setAwarded({});
    setNotes('');
    setAiScore(null);
    setSuggestion(null);
  }, [openId]);

  const usingRubric = rubric.length > 0 && aiScore === null;
  const rubricTotal = rubric.reduce((s, c) => s + (awarded[c.id] ?? 0), 0);
  const singleScore = awarded['__single'] ?? 0;
  const maxPoints = active?.maxPoints ?? 1;
  const rawTotal = aiScore !== null ? aiScore : usingRubric ? rubricTotal : singleScore;
  const total = Math.min(rawTotal, maxPoints);

  const step = (id: string, delta: number, max: number) =>
    setAwarded((prev) => {
      setAiScore(null);
      const next = Math.max(0, Math.min(max, (prev[id] ?? 0) + delta));
      return { ...prev, [id]: next };
    });

  const handleSuggest = async () => {
    if (!active) return;
    if (!canAiGrading) {
      openUpgrade(FEATURES.AI_GRADING);
      return;
    }
    try {
      const { data } = await aiSuggest.mutateAsync({
        attemptId: active.attemptId,
        questionId: active.questionId,
        language: i18n.language,
      });
      setSuggestion(data);
    } catch (err: any) {
      const code = err?.response?.data?.code;
      toast.error(code === 'AI_GRADING_DISABLED' ? t('ai_grading_disabled') : t('ai_suggestion_failed'));
    }
  };
  const acceptAi = () => {
    if (!suggestion) return;
    setAwarded((prev) => ({ ...prev, __single: suggestion.suggestedScore }));
    setAiScore(suggestion.suggestedScore);
  };
  const adjustAi = () => {
    if (!suggestion) return;
    setAwarded((prev) => ({ ...prev, __single: suggestion.suggestedScore }));
    setAiScore(null);
  };

  /** The next still-pending row after `answerId`, in display order. */
  const nextPending = (answerId: string, done: Set<string>) => {
    const idx = ordered.findIndex((it) => it.answerId === answerId);
    return ordered.slice(idx + 1).find((it) => !done.has(it.answerId) && it.answerId !== answerId);
  };

  const handleSaveNext = async () => {
    if (!active) return;
    try {
      await gradeManual.mutateAsync({ answerId: active.answerId, score: total, notes: notes.trim() || undefined });
      const done = new Set(graded).add(active.answerId);
      setGraded(done);
      // Advance to the next still-pending answer, or close when the queue is clear.
      setOpenId(nextPending(active.answerId, done)?.answerId ?? null);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('ai_suggestion_failed'));
    }
  };

  const matchesAi = suggestion !== null && aiScore !== null && aiScore === suggestion.suggestedScore;
  const activeNumber = active ? numberOf(active) : 0;

  const footer =
    active && canGrade && !(active.writingTask || active.questionType === 'SPEAKING') ? (
      <div className="flex flex-col gap-2.5">
        <div className="flex items-baseline gap-2">
          <span data-grading-total className="text-[15px] font-bold tabular-nums" style={{ color: M.ink }}>
            {t('grading_total')} {total} / {maxPoints}
          </span>
          {matchesAi && (
            <span className="text-[12.5px]" style={{ color: M.ink3 }}>
              {t('grading_matches_ai')}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="lg"
            className="flex-1 px-3"
            onClick={() => setOpenId(nextPending(active.answerId, graded)?.answerId ?? null)}
          >
            {t('grading_skip')}
          </Button>
          {/* The grader's main action takes the lime; accepting the AI score stays secondary. */}
          <Button size="lg" className="flex-1 gap-1.5 px-3" onClick={handleSaveNext} disabled={gradeManual.isPending}>
            {gradeManual.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            {t('grading_save_next')}
          </Button>
        </div>
      </div>
    ) : undefined;

  return (
    <MobileScreen>
      <ScreenHeader title={t('nav.grading')} count={!isLoading && remaining > 0 ? remaining : undefined}>
        {!isLoading && sessionTotal > 0 && (
          <div data-grading-progress className="flex items-center gap-2.5 pt-3">
            <span
              role="progressbar"
              aria-label={t('grading_session_graded')}
              aria-valuemin={0}
              aria-valuemax={sessionTotal}
              aria-valuenow={gradedCount}
              className="h-[5px] flex-1 overflow-hidden rounded-full"
              style={{ background: M.muted }}
            >
              <span className="block h-full rounded-full" style={{ width: `${progressPct}%`, background: M.ink }} />
            </span>
            <span className="whitespace-nowrap text-[12.5px] tabular-nums" style={{ color: M.ink3 }}>
              {t('grading_session_short', { graded: gradedCount, total: sessionTotal })}
            </span>
          </div>
        )}
        <div className="pb-3 pt-2.5">
          <Segmented
            full
            aria-label={t('grading_queue')}
            tabs={[
              { value: 'question', label: t('grading_by_question') },
              { value: 'respondent', label: t('grading_by_respondent') },
            ]}
            value={mode}
            onChange={(value) => setMode(value as GradingMode)}
          />
        </div>
      </ScreenHeader>

      {isLoading ? (
        <MobileSkeleton />
      ) : items.length === 0 ? (
        <MobileEmpty icon={<CircleCheck size={22} />} title={t('no_pending_grading')} />
      ) : (
        <ScreenBody>
          <div className="space-y-3">
            {groups.map((group) => {
              const first = group.items[0];
              const title =
                mode === 'question'
                  ? `${t('grading_question_n', { n: group.questionNumber })} · ${t(questionTypeLabelKey(first.questionType))}`
                  : group.name;
              return (
                <Panel
                  key={group.key}
                  title={title}
                  aside={<span className="tabular-nums">{t('grading_n_answers', { count: group.items.length })}</span>}
                  flush
                >
                  <ul className="divide-y divide-border">
                    {group.items.map((it) => {
                      const done = graded.has(it.answerId);
                      const label =
                        mode === 'question'
                          ? it.candidateName
                          : `${t('grading_question_n', { n: numberOf(it) })} · ${t(questionTypeLabelKey(it.questionType))}`;
                      const content = (
                        <>
                          {mode === 'question' && <Avatar name={it.candidateName} size="xs" className="h-7 w-7 text-[11px]" />}
                          <span className="min-w-0 flex-1 truncate text-[15px] font-medium" style={{ color: M.ink }}>
                            {label}
                          </span>
                          <GradingStatus graded={done} className="text-[12.5px]" />
                        </>
                      );
                      return (
                        <li key={it.answerId}>
                          {done ? (
                            // Graded this session: shown until the queue refetches, not reopened.
                            <div data-grading-row={it.answerId} className="flex min-h-12 items-center gap-2.5 px-3.5 py-2.5">
                              {content}
                            </div>
                          ) : (
                            <button
                              type="button"
                              data-grading-row={it.answerId}
                              onClick={() => setOpenId(it.answerId)}
                              className="flex min-h-12 w-full items-center gap-2.5 px-3.5 py-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                            >
                              {content}
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </Panel>
              );
            })}
          </div>
        </ScreenBody>
      )}

      {/* Full-screen grader */}
      <FullScreen
        open={Boolean(active)}
        onClose={() => setOpenId(null)}
        closeIcon="x"
        title={active?.candidateName || ''}
        right={
          active ? (
            <span className="text-[12.5px] tabular-nums" style={{ color: M.ink3 }}>
              {t('grading_question_n', { n: activeNumber })}
            </span>
          ) : undefined
        }
        footer={footer}
      >
        {active && (active.writingTask || active.questionType === 'SPEAKING') && canGrade ? (
          // IELTS-style Writing / Speaking: criterion bands instead of points.
          <IeltsBandGrader
            attemptId={active.attemptId}
            questionId={active.questionId}
            answerId={active.answerId}
            task={active.writingTask ?? 'SPEAKING'}
            recordings={active.speakingRecordings}
            aiAvailable={active.aiAvailable}
            script={answerToText(active.value)}
            suggested={active.aiBandCriteria}
            suggestedFeedback={active.aiReasoning}
            canUseAi={canAiGrading}
            onUpgrade={() => openUpgrade(FEATURES.AI_GRADING)}
            onSkip={() => setOpenId(null)}
            onGraded={() => {
              setGraded((g) => new Set(g).add(active.answerId));
              setOpenId(null);
            }}
          />
        ) : (
          active && (
            <div className="space-y-4 px-4 pb-6 pt-4">
              <div className="space-y-1.5">
                <p data-grading-meta className="text-[12.5px]" style={{ color: M.ink3 }}>
                  {[
                    t('grading_question_n', { n: activeNumber }),
                    t(questionTypeLabelKey(active.questionType)),
                    t('n_points', { count: maxPoints }),
                  ].join(' · ')}
                </p>
                <p className="text-[15px] font-medium leading-relaxed" style={{ color: M.ink }}>
                  {active.questionText}
                </p>
              </div>
              <div className="rounded-lg px-3.5 py-3" style={{ border: `1px solid ${M.border}`, background: M.bg }}>
                <p className="text-[12px] font-medium" style={{ color: M.ink3 }}>
                  {answerLabel(active, t)}
                </p>
                <p className="mt-1.5 whitespace-pre-wrap text-[14.5px] leading-relaxed" style={{ color: M.ink }}>
                  {answerToText(active.value)}
                </p>
              </div>

              {canGrade && (
                <>
                  <AiSuggestionBox
                    density="phone"
                    suggestion={suggestion}
                    matchesAi={matchesAi}
                    requesting={aiSuggest.isPending}
                    canUseAi={canAiGrading}
                    onRequest={handleSuggest}
                    onAccept={acceptAi}
                    onAdjust={adjustAi}
                  />
                  <GradingRubric density="phone" criteria={rubric} awarded={awarded} maxPoints={maxPoints} onStep={step} />
                  <GraderNote id="grading-note-mobile" value={notes} onChange={setNotes} rows={3} />
                </>
              )}
            </div>
          )
        )}
      </FullScreen>
    </MobileScreen>
  );
}

export default GradingMobile;
